import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { useQuery } from 'react-query'
import {
  Box,
  Flex,
  Text,
  Input,
  Select,
  Checkbox,
  FormControl,
  FormLabel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure
} from '@chakra-ui/react'
import Axios from '../../../Axios'
import grades from '../../../Utils/SelectList/GradesCarabineros'
import {
  Button,
  TableBox,
  Loading,
  currentMonth,
  money,
  get,
  writers,
  useBillingAction
} from './shared'

const emptyRate = () => ({
  grade: '',
  amount: '',
  from: currentMonth(),
  enabled: true,
  note: ''
})
const normalize = (value) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toUpperCase()

export default function Rates() {
  const { user } = useSelector((s) => s.auth)
  const canWrite = writers.includes(user?.role)
  const { busy, action } = useBillingAction()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [rate, setRate] = useState(emptyRate)
  const [search, setSearch] = useState('')
  const [showHistory, setShowHistory] = useState(false)
  const rates = useQuery(['billing', 'rates'], () => get('rates'), {
    retry: false
  })
  const all = rates.data || []
  // Display one effective rate per grade, while keeping scheduled and previous versions accessible.
  const month = currentMonth()
  const groups = [...new Set(all.map((r) => normalize(r.grade)))]
  const rows = (
    showHistory
      ? all
      : groups.map((key) => {
          const versions = all
            .filter((r) => normalize(r.grade) === key)
            .sort(
              (a, b) =>
                b.from.localeCompare(a.from) || b._id.localeCompare(a._id)
            )
          return (
            versions.find((r) => r.from <= month) ||
            versions[versions.length - 1]
          )
        })
  ).filter((r) => normalize(r.grade).includes(normalize(search)))
  const edit = (existing) => {
    setRate(
      existing
        ? {
            grade: existing.grade,
            amount: existing.amount,
            from: month,
            enabled: existing.enabled,
            note: ''
          }
        : emptyRate()
    )
    onOpen()
  }
  const save = (e) => {
    e.preventDefault()
    action(async () => {
      await Axios.post('/billing/rates', {
        ...rate,
        amount: Number(rate.amount)
      })
      onClose()
    }, 'Descuento guardado')
  }
  return (
    <Box>
      <Flex align="center" justify="space-between" gap={4} wrap="wrap" mb={5}>
        <Text color="gray.600" maxW="650px">
          Define el descuento mensual de cada rango y desde cuándo se aplica.
        </Text>
        {canWrite && <Button onClick={() => edit()}>Agregar rango</Button>}
      </Flex>
      <Flex
        gap={4}
        align="center"
        wrap="wrap"
        pb={4}
        borderBottom="1px solid"
        borderColor="green.500"
      >
        <Input
          aria-label="Buscar rango"
          placeholder="Buscar rango"
          maxW="350px"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Checkbox
          colorScheme="green"
          isChecked={showHistory}
          onChange={(e) => setShowHistory(e.target.checked)}
        >
          Ver todas las vigencias
        </Checkbox>
      </Flex>
      <Loading query={rates} />
      <TableBox>
        <Table>
          <Thead>
            <Tr>
              <Th>Rango</Th>
              <Th isNumeric>Monto de descuento</Th>
              <Th>Vigente desde</Th>
              <Th>Estado</Th>
              {showHistory && <Th>Motivo</Th>}
              {canWrite && <Th>Opciones</Th>}
            </Tr>
          </Thead>
          <Tbody>
            {!rates.isLoading && !rows.length && (
              <Tr>
                <Td colSpan={6} py={8} textAlign="center" color="gray.500">
                  {search
                    ? 'No hay rangos que coincidan con la búsqueda.'
                    : 'Aún no hay rangos con descuento definido.'}
                </Td>
              </Tr>
            )}
            {rows.map((r) => (
              <Tr key={r._id}>
                <Td fontWeight="semibold">{r.grade}</Td>
                <Td isNumeric>{money(r.amount)}</Td>
                <Td>{r.from}</Td>
                <Td>
                  <Badge
                    colorScheme={
                      !r.enabled ? 'gray' : r.from > month ? 'blue' : 'green'
                    }
                  >
                    {!r.enabled
                      ? 'Deshabilitado'
                      : r.from > month
                      ? 'Programado'
                      : 'Habilitado'}
                  </Badge>
                </Td>
                {showHistory && <Td>{r.note || '—'}</Td>}
                {canWrite && (
                  <Td>
                    <Button
                      size="sm"
                      variant="ghost"
                      bg="transparent"
                      color="green.500"
                      onClick={() => edit(r)}
                    >
                      Cambiar descuento
                    </Button>
                  </Td>
                )}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableBox>
      <Modal
        isOpen={isOpen}
        onClose={busy ? () => {} : onClose}
        isCentered
        size="lg"
      >
        <ModalOverlay />
        <ModalContent borderRadius={20} mx={4} as="form" onSubmit={save}>
          <ModalHeader color="green.500" fontSize="2xl">
            {rate.grade ? 'Descuento por rango' : 'Agregar rango'}
          </ModalHeader>
          <ModalCloseButton isDisabled={busy} />
          <ModalBody>
            <FormControl isRequired mb={4}>
              <FormLabel htmlFor="rate-grade" color="green.500">
                Rango
              </FormLabel>
              <Input
                id="rate-grade"
                list="member-grades"
                value={rate.grade}
                maxLength={80}
                onChange={(e) => setRate({ ...rate, grade: e.target.value })}
              />
              <datalist id="member-grades">
                {[
                  ...new Set([
                    ...grades.filter((g) => g !== 'Sin definir'),
                    ...all.map((r) => r.grade)
                  ])
                ].map((g) => (
                  <option key={g} value={g} />
                ))}
              </datalist>
            </FormControl>
            <FormControl isRequired={rate.enabled} mb={4}>
              <FormLabel htmlFor="rate-amount" color="green.500">
                Monto de descuento mensual ($)
              </FormLabel>
              <Input
                id="rate-amount"
                type="number"
                min="1"
                max="9999999"
                step="1"
                isDisabled={!rate.enabled}
                value={rate.amount}
                onChange={(e) => setRate({ ...rate, amount: e.target.value })}
              />
            </FormControl>
            <FormControl isRequired mb={4}>
              <FormLabel htmlFor="rate-from" color="green.500">
                Vigente desde
              </FormLabel>
              <Input
                id="rate-from"
                type="month"
                value={rate.from}
                onChange={(e) => setRate({ ...rate, from: e.target.value })}
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel htmlFor="rate-note" color="green.500">
                Motivo del cambio
              </FormLabel>
              <Input
                id="rate-note"
                maxLength={500}
                value={rate.note}
                onChange={(e) => setRate({ ...rate, note: e.target.value })}
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel htmlFor="rate-enabled" color="green.500">
                Estado
              </FormLabel>
              <Select
                id="rate-enabled"
                value={String(rate.enabled)}
                onChange={(e) =>
                  setRate({ ...rate, enabled: e.target.value === 'true' })
                }
              >
                <option value="true">Habilitado</option>
                <option value="false">Deshabilitado</option>
              </Select>
            </FormControl>
            <Text fontSize="sm" color="gray.600">
              Los cambios conservan el historial y no modifican las nóminas ya
              guardadas.
            </Text>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button
              bg="beige.500"
              color="green.500"
              _hover={{ bg: 'beige.600' }}
              onClick={onClose}
              isDisabled={busy}
            >
              Cancelar
            </Button>
            <Button type="submit" isLoading={busy}>
              Guardar descuento
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}
